/**
 * English Translation
 */

const enUSTranslations = {
  // Application basic information
  app: {
    title: 'Overtime Management Tool',
    subtitle: 'Overtime Management System',
    description: 'Professional overtime recording and time-off management tool'
  },

  // DateTime related
  dateTime: {
    invalidDate: 'Invalid Date',
    formatError: 'Date Format Error'
  },

  // Common vocabulary
  common: {
    confirm: 'Confirm',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    copy: 'Copy',
    close: 'Close',
    loading: 'Loading...',
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    info: 'Info',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    submit: 'Submit',
    reset: 'Reset',
    clear: 'Clear',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    export: 'Export',
    import: 'Import',
    settings: 'Settings',
    help: 'Help',
    about: 'About'
  },
  
  // Time related
  time: {
    startTime: 'Start Time',
    endTime: 'End Time',
    duration: 'Duration',
    totalTime: 'Total Time',
    remainingTime: 'Remaining Time',
    usedTime: 'Used Time',
    availableTime: 'Available Time',
    hours: 'Hours',
    minutes: 'Minutes',
    days: 'Days',
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    thisYear: 'This Year'
  },
  
  // Overtime related
  overtime: {
    title: 'Overtime Records',
    record: 'Record Overtime',
    startTime: 'Overtime Start Time',
    endTime: 'Overtime End Time',
    duration: 'Overtime Duration',
    remaining: 'Remaining Time Off',
    total: 'Total Overtime',
    list: 'Overtime Records List',
    details: 'Detailed Records',
    consumption: 'Consumption Records',
    edit: 'Edit Overtime Record',
    delete: 'Delete Overtime Record',
    copy: 'Copy Record',
    
    // Form validation
    validation: {
      startTimeRequired: 'Please enter overtime start time!',
      endTimeRequired: 'Please enter overtime end time!',
      endTimeAfterStart: 'Overtime end time must be after start time!'
    },
    
    // Statistics
    stats: {
      totalHours: 'You have worked {{hours}} overtime hours this year',
      remainingHours: 'Remaining time off: {{overtimeHours}}+{{annualLeaveHours}} hours'
    }
  },
  
  // Time off related
  rest: {
    title: 'Time Off Records',
    record: 'Request Time Off',
    startTime: 'Time Off Start Time',
    endTime: 'Time Off End Time',
    duration: 'Time Off Duration',
    type: 'Time Off Type',
    compTime: 'Comp Time',
    list: 'Time Off Records List',
    edit: 'Edit Time Off Record',
    delete: 'Delete Time Off Record',
    
    // Form validation
    validation: {
      startTimeRequired: 'Please enter time off start and end time!',
      endTimeRequired: 'Please enter time off end time!',
      endTimeAfterStart: 'Time off end time must be after start time!',
      insufficientTime: 'Insufficient remaining time for this time off request!',
      insufficientOvertimeTime: 'Insufficient overtime hours to increase comp time!'
    }
  },
  
  // Annual leave related
  annualLeave: {
    title: 'Annual Leave Management',
    totalDays: 'Total Annual Leave Days',
    usedHours: 'Used Hours',
    remainingHours: 'Remaining Hours',
    record: 'Annual Leave Records',
    set: 'Set Annual Leave',
    edit: 'Edit Annual Leave Record',
    delete: 'Delete Annual Leave Record',
    
    // Settings dialog
    settings: {
      title: 'Set Annual Leave',
      totalDaysLabel: 'Total Annual Leave Days',
      totalDaysPlaceholder: 'Enter total annual leave days',
      note: 'Note: Annual leave is calculated as 8 hours per day'
    }
  },
  
  // Calculation rules
  rules: {
    title: 'Calculation Rules',
    overtime: {
      title: 'Overtime Calculation Rules',
      description: 'Calculated by continuous time, including lunch break',
      example: 'Example: {{timeRange}} = {{result}}',
      note: 'Overtime is calculated by actual continuous time, lunch break not deducted'
    },
    rest: {
      title: 'Time Off Calculation Rules',
      description: 'Calculated by working days, lunch break deducted',
      example: 'Example: {{timeRange}} = {{result}} ({{note}})',
      note: '1 hour lunch break deducted',
      workingHours: 'Working hours: 9:00-18:00 (excluding 12:00-13:00 lunch break)'
    },
    annualLeave: {
      title: 'Annual Leave Calculation Rules',
      description: 'Annual leave is calculated as 8 hours per day',
      example: '1 day annual leave = 8 hours'
    }
  },
  
  // Data management
  data: {
    save: 'Save Data Locally',
    load: 'Load Backup Data',
    export: 'Export Data',
    import: 'Import Data',
    backup: 'Backup Data',
    restore: 'Restore Data',
    clear: 'Clear Data',
    
    // Messages
    messages: {
      saveSuccess: 'Data saved successfully',
      loadSuccess: 'Data imported and updated successfully',
      exportSuccess: 'Data exported successfully',
      importSuccess: 'Data imported successfully',
      clearSuccess: 'Data cleared successfully',
      parseError: 'Unable to parse imported file. Please ensure it is in valid format.',
      newerDataWarning: 'Imported data is older than local data. Do you still want to overwrite local data?',
      cleanupComplete: 'Cleanup completed, {{count}} redundant records removed.'
    }
  },
  
  // Table related
  table: {
    index: 'Index',
    type: 'Type',
    startTime: 'Start Time',
    endTime: 'End Time',
    duration: 'Duration',
    remaining: 'Remaining',
    actions: 'Actions',
    details: 'Detailed Records',
    expand: 'Expand',
    collapse: 'Collapse',
    noData: 'No Data',
    
    // Pagination
    pagination: {
      total: 'Total {{total}} items',
      pageSize: 'Items per page',
      items: 'items',
      page: 'page'
    },

    // Additional table fields
    number: 'No.',
    remainingTime: 'Remaining Time Off Hours',
    consumptionRecords: 'Consumption Records',
    restRequestStartTime: 'Rest Request Start Time',
    restRequestEndTime: 'Rest Request End Time',
    actualConsumptionStartTime: 'Actual Consumption Start Time',
    actualConsumptionEndTime: 'Actual Consumption End Time',
    deductionTime: 'Deduction Time',
    annualLeaveStartTime: 'Annual Leave Start Time',
    annualLeaveEndTime: 'Annual Leave End Time'
  },



  // Statistics
  stats: {
    totalOvertime: 'Total Work Beast Time',
    availableOvertime: 'Available Work Beast Time',
    remainingAnnualLeave: 'Remaining Big Time Off',
    totalRest: 'Total Slacking Time'
  },

  // Actions
  actions: {
    recordOvertime: 'Record Work Beast Event',
    addOvertimeTime: 'Add Work Beast Record',
    requestRest: 'Request Slacking',
    useOvertimeTime: 'Start Slacking~~',
    setAnnualLeave: 'Set Big Time Off',
    configureAnnualLeaveDays: 'Configure Annual Big Time Off',
    recordAnnualLeave: 'Record Big Time Off',
    saveData: 'Save Work Beast Records',
    exportData: 'Export Work Beast Records',
    importData: 'Import Work Beast Records',
    cleanData: 'Clean Duplicate Work Beast Records'
  },

  // Messages
  messages: {
    overtimeAdded: 'Work Beast record added',
    restAdded: 'Slacking record added',
    overtimeDeleted: 'Work Beast record deleted',
    restDeleted: 'Slacking record deleted',
    dataSaved: 'Data saved',
    dataLoaded: 'Data loaded',
    dataImported: 'Data imported',
    dataExported: 'Data exported',
    noLocalData: 'No local data found, using default data',
    cleanupComplete: 'Cleanup complete, removed {{count}} redundant records',
    overtimeRecordAdded: 'Work Beast record added successfully!',
    restRequestSubmitted: 'Slacking application submitted successfully!',
    pleaseCompleteTimeInfo: 'Please complete the time information',
    overtimeRecordUpdated: 'Work Beast record updated successfully!',
    restRecordUpdated: 'Slacking record updated successfully!',
    annualLeaveRecordUpdated: 'Big Time Off record updated successfully!',
    annualLeaveRecordAdded: 'Big Time Off record added successfully!',
    copiedToClipboard: 'Copied to clipboard',
    copyFailed: 'Copy failed, please try again'
  },

  // Modals
  modals: {
    recordOvertimeTitle: 'Record Work Beast Event',
    requestRestTitle: 'Slacking Application Form',
    setAnnualLeaveTitle: '🏖️ Set Big Time Off',
    recordOvertimeTimeTitle: '⏱️ Record Work Beast Time',
    requestRestTimeTitle: '😴 Request Slacking Time',
    editOvertimeTitle: 'Edit Work Beast Record',
    editRestTitle: 'Edit Slacking Record',
    editAnnualLeaveTitle: 'Edit Big Time Off Record',
    recordAnnualLeaveTitle: '🏖️ Record Big Time Off',
    editRecordTitle: 'Edit Record'
  },

  // Forms
  forms: {
    currentAnnualLeave: 'Current Big Time Off:',
    remainingTime: 'Remaining Big Time:',
    customDays: 'Custom Big Time:',
    enterDays: 'Enter Big Time',
    noAnnualLeave: 'No Big Time Off',
    description: 'Description:',
    descriptionPlaceholder: 'Optional description',
    annualLeaveStartTime: 'Big Time start time:',
    annualLeaveEndTime: 'Big Time end time:',
    overtimeStartTime: 'Work Beast start time:',
    overtimeEndTime: 'Work Beast end time:'
  },

  // Confirm dialogs
  confirmDialog: {
    deleteMessage: 'Are you sure you want to delete this record?',
    cleanupMessage: 'Are you sure you want to clean up redundant data? This operation cannot be undone.',
    deleteRestMessage: 'Are you sure you want to delete this slacking record?',
    deleteAnnualLeaveMessage: 'Are you sure you want to delete this annual leave record?'
  },

  // Records
  records: {
    overtimeRecords: 'Work Beast Records',
    restRecords: 'Slacking Records',
    annualLeaveRecords: 'Big Time Records',
    restType: 'Time Off',
    annualLeaveType: 'Big Time Off',
    annualLeave: 'Big Time Off',
    rest: 'Slacking',
    overtime: 'Work Beast'
  },

  // Pagination
  pagination: {
    previous: 'Previous',
    next: 'Next',
    pageInfo: 'Page {{current}} of {{total}}',
    itemsPerPage: '{{count}} items/page'
  },

  // Copy function
  copy: {
    deductOvertimeTotal: 'Overtime comp off, deducted overtime hours ({{hours}} hours)',
    deductAnnualLeaveTotal: 'Deducted annual leave hours ({{hours}} hours)',
    annualLeaveTitle: 'Annual Leave:',
    errorPrefix: 'Unable to copy text: '
  },

  // Consumption records
  consumption: {
    title: 'Time Off Consumption Records',
    restStartTime: 'Time Off Request Start Time',
    restEndTime: 'Time Off Request End Time',
    actualStartTime: 'Actual Consumption Start Time',
    actualEndTime: 'Actual Consumption End Time',
    deductionTime: 'Time Off Deduction Time',
    type: 'Type',
    
    // Copy report
    copyReport: {
      overtimeDeduction: 'Deducted corresponding overtime hours ({{hours}} hours)',
      annualLeaveDeduction: 'Deducted annual leave hours ({{hours}} hours)',
      copySuccess: 'Copied to clipboard',
      copyFailed: 'Copy failed, please try again'
    }
  },
  
  // Update log
  updateLog: {
    title: 'Update Log',
    show: 'Show Update Log',
    hide: 'Hide Update Log',
    version: 'Version',
    date: 'Date',
    content: 'Update Content'
  },
  
  // Language settings
  language: {
    title: 'Language Settings',
    current: 'Current Language',
    select: 'Select Language',
    change: 'Change Language'
  },
  
  // Error messages
  errors: {
    networkError: 'Network error, please check your connection',
    serverError: 'Server error, please try again later',
    unknownError: 'Unknown error, please contact administrator',
    validationError: 'Data validation failed',
    permissionDenied: 'Permission denied',
    notFound: 'Data not found',
    timeout: 'Request timeout, please try again'
  },

  // Theme related
  theme: {
    toggle: 'Toggle Theme',
    modern: 'Modern Style',
    retro: 'Retro Style',
    sketch: 'Sketch Style',
    damaged: 'Damaged Style',
    switchToModern: 'Switch to Modern Style',
    switchToRetro: 'Switch to Retro Style',
    switchToSketch: 'Switch to Sketch Style',
    switchToDamaged: 'Switch to Damaged Style'
  }
};

export default enUSTranslations;
